// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'

const routes = [
  { path: '/onboarding', name: 'onboarding', component: () => import('../pages/OnBoardingPage.vue') },
  { path: '/terms-and-conditions', name: 'termsAndConditions', component: () => import('../pages/TermsAndConditionsPage.vue') },
  { path: '/terms-view', name: 'termsView', component: () => import('../pages/TermsViewPage.vue') },
  { path: '/', name: 'login', component: () => import('../pages/LoginPage.vue') },
  { path: '/register', name: 'register', component: () => import('../pages/RegisterPage.vue') },
  { path: '/welcome', name: 'welcome', component: () => import('../pages/WelcomePage.vue') },
  { path: '/home', name: 'home', component: () => import('../pages/HomePage.vue') },
  { path: '/user-active-sites', name: 'userActiveSites', component: () => import('../pages/UserActiveSitesPage.vue') },
  { path: '/drafts', name: 'drafts', component: () => import('../pages/DraftsPage.vue') },
  { path: '/ai-generation', name: 'ai-generation', component: () => import('../pages/AiGenerationPage.vue') },
  { path: '/new-drafts', name: 'newDrafts', component: () => import('../pages/NewDraftsPage.vue') },
  { path: '/new-ai-generation', name: 'newAiGeneration', component: () => import('../pages/NewAiGenerationPage.vue') },
  { path: '/new-side-wall-generation', name: 'newSideWallGeneration', component: () => import('../pages/NewSideWallGenerationPage.vue') },
  { path: '/ai-test', name: 'ai-test', component: () => import('../pages/AiTest.vue') },
  { path: '/trends', name: 'trends', component: () => import('../pages/TrendsPage.vue') },
  { path: '/side-wall-generation', name: 'sideWallGeneration', component: () => import('../pages/SideWallGenerationPage.vue') },
  { path: '/shooting-guide', name: 'shootingGuide', component: () => import('../pages/ShootingGuidePage.vue') },
  { path: '/plate-editor', name: 'plateEditor', component: () => import('../pages/PlateEditorPage.vue') },
  { path: '/mypage', name: 'my', component: () => import('../pages/MyPage.vue') },
  { path: '/licenses', name: 'licenses', component: () => import('../pages/LicensesPage.vue') },
  { path: '/image-history', name: 'imageHistory', component: () => import('../pages/ImageHistoryPage.vue') },
  { path: '/reset-password', name: 'resetPassword', component: () => import('../pages/ResetPasswordPage.vue') },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 프로덕션 빌드 업데이트 후 발생하는 동적 모듈 import 에러 처리
// 서버에서 코드 업데이트 시 기존 사용자의 청크 파일이 없어져서 발생하는 에러를 자동 새로고침으로 해결
router.onError((error, to) => {
  const dynamicImportErrors = [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'error loading dynamically imported module',
    'Unable to preload CSS'
  ];

  const isDynamicImportError = dynamicImportErrors.some(msg =>
    error.message?.toLowerCase().includes(msg.toLowerCase())
  );

  if (isDynamicImportError) {
    console.warn('[Router] Dynamic import failed, reloading page for new version...');
    // 무한 새로고침 방지: sessionStorage에 리로드 횟수 저장
    const reloadKey = 'dynamic_import_reload_' + to.fullPath;
    const reloadCount = parseInt(sessionStorage.getItem(reloadKey) || '0');

    if (reloadCount < 2) {
      sessionStorage.setItem(reloadKey, String(reloadCount + 1));
      window.location.href = to.fullPath;
    } else {
      // 2번 이상 리로드 시도했으면 캐시 완전 클리어 후 새로고침
      sessionStorage.removeItem(reloadKey);
      console.error('[Router] Multiple reload attempts failed, forcing hard reload...');
      window.location.reload(true);
    }
  }
})

router.beforeEach(async (to, from, next) => {
  // 인증이 필요없는 페이지들
  const publicPages = ['login', 'register', 'termsAndConditions', 'termsView', 'privacyView', 'marketingView', 'resetPassword'];
  const authRequired = !publicPages.includes(to.name);

  if (authRequired) {
    // 보호된 페이지에 인증되지 않은 사용자가 접근하면 로그인 페이지로
    // 인증 상태 확인
    const authStore = (await import('@common/js/stores/auth')).default();
    await authStore.validateAuth();
    if (!authStore.isAuthenticated) {
      next('/');
      return;
    }

    // 오픈소스 라이선스 페이지는 관리자만 접근 가능
    const userRole = authStore.user?.role;
    if (to.name === 'licenses' && userRole !== 'ADMIN') {
      next('/');
      return;
    }

    // SALES_PERSON 권한 확인 (영업사원만 앱 접근 가능)
    if (userRole !== 'SALES_PERSON') {
      await authStore.logout();
      next('/');
      return;
    }

    // 프로필이 완성되지 않은 사용자는 마이페이지만 접근 가능
    if (!authStore.isProfileComplete && to.name !== 'my') {
      next({ name: 'my' });
      return;
    }

    // sales15@noroo.com 은 기존 시안/AI 생성 페이지 대신 new 페이지로 이동
    if (authStore.user?.email === 'sales15@noroo.com') {
      if (to.name === 'drafts') {
        next({ name: 'newDrafts', query: to.query, hash: to.hash, replace: true });
        return;
      }
      if (to.name === 'ai-generation') {
        next({ name: 'newAiGeneration', query: to.query, hash: to.hash, replace: true });
        return;
      }
    }
  }

  // directly URL access check
  if (to.meta.requiresDirectAccess && !from.name) {
    next('/');
    return;
  }

  // Check if onboarding has been visited
  if (to.name === 'home') {
    const hasVisitedOnboarding = localStorage.getItem('hasVisitedOnboarding');
    if (!hasVisitedOnboarding) {
      next({ name: 'onboarding' });
      return;
    }
  }

  next();
});

export default router
